import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { handleSupabaseError } from '../lib/errors'
import { Segment, CreateSegment, UpdateSegment } from '../types'

export function useSegments(runId: string) {
  return useQuery({
    queryKey: ['segments', runId],
    queryFn: async (): Promise<Segment[]> => {
      const { data, error } = await supabase
        .from('segments')
        .select('*')
        .eq('run_id', runId)
        .order('order_index')
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    enabled: !!runId
  })
}

export function useCreateSegment() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (newSegment: CreateSegment): Promise<Segment> => {
      const { data, error } = await supabase
        .from('segments')
        .insert(newSegment)
        .select()
        .single()
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['segments', data.run_id] })
      queryClient.invalidateQueries({ queryKey: ['runs', data.run_id] })
    }
  })
}

export function useUpdateSegment() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateSegment }): Promise<Segment> => {
      const { data, error } = await supabase
        .from('segments')
        .update(updates)
        .eq('id', id)
        .select()
        .single()
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['segments', data.run_id] })
      queryClient.invalidateQueries({ queryKey: ['runs', data.run_id] })
    }
  })
}

export function useDeleteSegment() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (id: string): Promise<string> => {
      // Get the segment first to know which run to invalidate
      const { data: segment } = await supabase
        .from('segments')
        .select('run_id')
        .eq('id', id)
        .single()
      
      const { error } = await supabase
        .from('segments')
        .delete()
        .eq('id', id)
      
      if (error) throw handleSupabaseError(error)
      return segment!.run_id
    },
    onSuccess: (runId) => {
      queryClient.invalidateQueries({ queryKey: ['segments', runId] })
      queryClient.invalidateQueries({ queryKey: ['runs', runId] })
    }
  })
}

export function useReorderSegments() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ runId, segmentIds }: { runId: string; segmentIds: string[] }) => {
      // Update order_index for each segment
      const updates = segmentIds.map((id, index) => 
        supabase
          .from('segments')
          .update({ order_index: index })
          .eq('id', id)
      )
      
      await Promise.all(updates)
    },
    onSuccess: (_, { runId }) => {
      queryClient.invalidateQueries({ queryKey: ['segments', runId] })
      queryClient.invalidateQueries({ queryKey: ['runs', runId] })
    }
  })
}